import system from '@rebass/components'
import * as React from 'react'
import styled from 'styled-components'

export enum InputVariant {
  Horizontal = 'horizontal',
  Vertical = 'vertical',
}

const FormGroupContainer = styled.div<{
  variant: InputVariant
  align?: string
  stretch?: string
}>`
  display: flex;
  flex-direction: ${(props) =>
    props.variant === InputVariant.Horizontal ? 'row' : 'column'};
  align-items: ${(props) =>
    props.variant === InputVariant.Horizontal
      ? 'flex-start'
      : props.stretch
      ? props.stretch
      : 'center'};
  justify-content: ${(props) => (props.align ? props.align : 'center')};
  margin: 1rem;
`

FormGroupContainer.defaultProps = {
  variant: InputVariant.Vertical,
}

const FormGroupLabelDiv = styled.div<{ variant: InputVariant }>`
  margin-bottom: ${(props) =>
    props.variant === InputVariant.Horizontal ? '0.5rem' : '0.5rem'};
  margin-right: ${(props) =>
    props.variant === InputVariant.Horizontal ? '1.25rem' : '0'};
  width: ${(props) =>
    props.variant === InputVariant.Horizontal ? '250px' : '100%'};
  font-size: 14pt;
  font-weight: 500;
  color: ${(props) => props.theme.colors.primary};
  text-align: ${(props) =>
    props.variant === InputVariant.Horizontal ? 'left' : 'center'};
`
FormGroupLabelDiv.defaultProps = {
  variant: InputVariant.Vertical,
}

const FormGroupLabel = styled.label`
  margin-bottom: 1rem;
`

const FormGroupHelperTextInvalid = styled.label`
  margin: 0.5rem 0 0 0.5rem;
  color: #a30000;
`

const FormGroupDetails = styled.div<{ variant?: InputVariant }>`
  font-size: 12pt;
  color: ${(props) => props.theme.colors.primary};
  margin-top: ${(props) =>
    props.variant === InputVariant.Horizontal ? '0' : '0.25rem'};
  margin-right: ${(props) =>
    props.variant === InputVariant.Horizontal ? '1.25rem' : '0'};
  text-align: ${(props) =>
    props.variant === InputVariant.Horizontal ? 'left' : 'center'};
`

const InputGroup = styled.div<{ variant: InputVariant }>`
  margin-top: ${(props) =>
    props.variant === InputVariant.Horizontal ? '0' : '1rem'};
  display: flex;
  flex-direction: ${(props) =>
    props.variant === InputVariant.Horizontal ? 'row' : 'column'};
  width: ${(props) =>
    props.variant === InputVariant.Horizontal ? '250px' : null};
`
InputGroup.defaultProps = {
  variant: InputVariant.Vertical,
}

interface FormGroupProps {
  fieldId: string
  children?: React.ReactNode
  className?: string
  label?: React.ReactNode
  details?: string
  helperTextInvalid?: string
  variant?: InputVariant
  align?: string
  stretch?: string
}

export const FormGroup: React.FunctionComponent<FormGroupProps> = (props) => {
  return (
    <FormGroupContainer
      variant={props.variant}
      align={props.align}
      stretch={props.stretch}
    >
      {props.label && (
        <FormGroupLabelDiv variant={props.variant}>
          <FormGroupLabel
            htmlFor={props.fieldId}
            dangerouslySetInnerHTML={{ __html: props.label }}
          />
          {props.variant === InputVariant.Horizontal && props.details && (
            <FormGroupDetails
              dangerouslySetInnerHTML={{ __html: props.details }}
              variant={props.variant}
            />
          )}
        </FormGroupLabelDiv>
      )}
      {props.variant !== InputVariant.Horizontal && props.details && (
        <FormGroupDetails
          dangerouslySetInnerHTML={{ __html: props.details }}
          variant={props.variant}
        />
      )}
      <InputGroup variant={props.variant}>{props.children}</InputGroup>
      {props.helperTextInvalid && (
        <FormGroupHelperTextInvalid>
          {props.helperTextInvalid}
        </FormGroupHelperTextInvalid>
      )}
    </FormGroupContainer>
  )
}

FormGroup.defaultProps = {
  variant: InputVariant.Vertical,
} as Partial<FormGroupProps>

const TextInput = styled.input`
  padding: 8px 16px 8px 16px;
  border: 2px solid ${(props) => props.theme.colors.primary};
  border-radius: 5px;
  display: inline-block;
  vertical-align: middle;
  appearance: none;
  flex: 1 0 auto;
`

interface TextFieldProps {
  isDisabled?: boolean
  isReadOnly?: boolean
  isValid?: boolean
  variant?: TextFieldVariant
  id: string
  name: string
  value: string
  onChange?: (e: React.FormEvent<HTMLInputElement>) => void
}

export enum TextFieldVariant {
  Text = 'text',
  Date = 'date',
  Email = 'email',
  Password = 'password',
}

export const TextField: React.FunctionComponent<TextFieldProps> = (props) => {
  return (
    <TextInput
      disabled={props.isDisabled}
      type={props.variant ? props.variant : TextFieldVariant.Text}
      id={props.id}
      name={props.name}
      value={props.value}
      onChange={props.onChange}
    />
  )
}

//Exclusive Input

export enum RadioVariant {
  Grid = 'grid',
  Select = 'select',
}

const RadioInput = system(
  {
    is: 'input',
    type: 'radio',
  },
  () => ({
    position: 'absolute',
    left: '-9999px',
  }),
)

const RadioContainer = styled.div`
  margin-bottom: 0.25rem;
`

const BaseLabelSelect = styled.label`
  --pad-x: 0.875rem;
  --pad-y: 0.6rem;
  --gap: 0.75rem;

  span {
    position: relative;
    display: block;
    text-align: left;
    padding: var(--pad-y) var(--pad-x);
    color: ${(p) => p.theme.colors.primary};
    border: 2px solid ${(p) => p.theme.colors.primary};
    border-radius: 8px;
    width: 100%;
    min-width: 180px;
    max-width: 600px;
    font-weight: 500;
    cursor: pointer;
    transition: background-color 0.12s, color 0.12s, box-shadow 0.12s;
    box-shadow: rgb(0 0 0 / 20%) 0 2px 2px -2px, rgb(0 0 0 / 15%) 2px 3px 2px 0,
      rgb(0 0 0 / 12%) 0 2px 8px 0;
  }

  input:focus-visible + span {
    box-shadow: 0 0 0 3px rgba(0, 0, 0, 0.12),
      0 0 0 4px ${(p) => p.theme.colors.primary};
  }

  input:disabled + span,
  input:disabled + span::before,
  input:disabled + span::after {
    opacity: 0.6;
    cursor: not-allowed;
  }
`
export const RadioLabelSelect = styled(BaseLabelSelect)`
  --dot: 1.1rem;

  span {
    padding-left: calc(var(--pad-x) + var(--dot) + var(--gap));
  }

  /* outer circle */
  span::before {
    content: '';
    position: absolute;
    left: var(--pad-x);
    top: 50%;
    transform: translateY(-50%);
    width: var(--dot);
    height: var(--dot);
    border: 2px solid ${(p) => p.theme.colors.primary};
    border-radius: 9999px;
    background: #fff;
    box-sizing: border-box;
  }

  /* checkmark */
  span::after {
    content: '✓';
    position: absolute;
    left: calc(var(--pad-x) + var(--dot) / 2);
    top: 50%;
    transform: translate(-50%, -50%);
    width: var(--dot);
    height: var(--dot);
    display: grid;
    place-items: center;
    font-size: 0.9rem;
    font-weight: 900;
    line-height: 1;
    color: ${(p) => p.theme.colors.primary};
    opacity: 0;
    transition: opacity 0.12s;
  }

  input:checked + span {
    color: #fff;
    background-color: ${(p) => p.theme.colors.primary};
  }
  input:checked + span::before {
    border-color: #fff;
    background: #fff;
  }
  input:checked + span::after {
    opacity: 1;
  }
`

export const CheckboxLabelSelect = styled(BaseLabelSelect)`
  --box: 1.1rem;

  span {
    padding-left: calc(var(--pad-x) + var(--box) + var(--gap));
  }

  span::before {
    content: '';
    position: absolute;
    left: var(--pad-x);
    top: 50%;
    transform: translateY(-50%);
    width: var(--box);
    height: var(--box);
    box-sizing: border-box;
    border: 2px solid ${(p) => p.theme.colors.primary};
    border-radius: 4px;
    background: white;
  }

  /* checked state */
  input:checked + span {
    color: white;
    background-color: ${(p) => p.theme.colors.primary};
  }
  input:checked + span::before {
    background-color: white;
    border-color: white;
    color: ${(p) => p.theme.colors.primary};
    display: grid;
    place-items: center;
    content: '✓';
    font-size: 0.9rem;
    font-weight: 900;
    line-height: 1;
  }
`

//Binary styles
const RadioLabel = styled.label<{ variant: RadioVariant }>`
  text-align: center;
  span {
    font-weight: 500;
    padding: 0.5rem 0.5rem;
    display: inline-block;
    color: ${(props) => props.theme.colors.primary};
    border: 2px solid ${(props) => props.theme.colors.primary};
    border-radius: 5px;
    width: 100%;
    min-width: ${(props) =>
      props.variant === RadioVariant.Select ? '180px' : '90px'};
    max-width: 600px;
    cursor: pointer;
    box-shadow: rgb(0 0 0 / 20%) 0px 2px 2px -2px,
      rgb(0 0 0 / 15%) 2px 3px 2px 0px, rgb(0 0 0 / 12%) 0px 2px 8px 0px;
  }

  input:checked + span {
    color: white;
    background-color: ${(props) => props.theme.colors.primary};
  }
`

RadioLabel.defaultProps = {
  variant: RadioVariant.Select,
}

interface RadioProps
  extends Omit<
    React.HTMLProps<HTMLInputElement>,
    'disabled' | 'label' | 'onChange' | 'type'
  > {
  className?: string
  id: string
  label?: string
  name: string
  value: string
  variant?: RadioVariant
  onChange?: (event: React.FormEvent<HTMLInputElement>) => void
}

export const Radio: React.FunctionComponent<RadioProps> = (props) => {
  const { variant } = props
  switch (variant) {
    case RadioVariant.Select:
      return (
        <RadioContainer>
          <RadioLabelSelect htmlFor={props.id}>
            <RadioInput
              id={props.id}
              name={props.name}
              value={props.value}
              checked={props.checked}
              onChange={props.onChange}
            />
            <span
              dangerouslySetInnerHTML={{
                __html: props.label,
              }}
            />
          </RadioLabelSelect>
        </RadioContainer>
      )
    case RadioVariant.Grid:
      return (
        <RadioContainer>
          <RadioLabel htmlFor={props.id} variant={variant}>
            <RadioInput
              id={props.id}
              name={props.name}
              value={props.value}
              checked={props.checked}
              onChange={props.onChange}
            />
            <span
              dangerouslySetInnerHTML={{
                __html: props.label,
              }}
            />
          </RadioLabel>
        </RadioContainer>
      )
    default:
      return <></>
  }
}
Radio.defaultProps = {
  variant: RadioVariant.Select,
} as Partial<RadioProps>

//Non-Exclusive Input

const MultiSelectInput = system(
  {
    is: 'input',
    type: 'checkbox',
  },
  () => ({
    position: 'absolute',
    left: '-9999px',
  }),
)

const MultiSelectContainer = styled.div`
  margin-bottom: 0.25rem;
`

interface MultiSelectProps
  extends Omit<
    React.HTMLProps<HTMLInputElement>,
    'disabled' | 'label' | 'onChange' | 'type'
  > {
  className?: string
  id: string
  label?: string
  name: string
  value: string
  onChange?: (event: React.FormEvent<HTMLInputElement>) => void
}

export const MultiSelect: React.FunctionComponent<MultiSelectProps> = (
  props,
) => {
  return (
    <MultiSelectContainer>
      <CheckboxLabelSelect htmlFor={props.id}>
        <MultiSelectInput
          id={props.id}
          name={props.name}
          value={props.value}
          checked={props.checked}
          onChange={props.onChange}
        />
        <span
          dangerouslySetInnerHTML={{
            __html: props.label,
          }}
        />
      </CheckboxLabelSelect>
    </MultiSelectContainer>
  )
}

const ScaleInput = styled.input`
  -webkit-appearance: none;
  width: 100%;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    border: 1px solid #000000;
    height: 36px;
    width: 16px;
    border-radius: 3px;
    background: #ffffff;
    cursor: pointer;
    margin-top: -14px; /* You need to specify a margin in Chrome, but in Firefox and IE it is automatic */
    box-shadow: 1px 1px 1px #000000, 0px 0px 1px #0d0d0d;
  }

  &::-webkit-slider-runnable-track {
    width: 100%;
    height: 8.4px;
    cursor: pointer;
    box-shadow: 1px 1px 1px #000000, 0px 0px 1px #0d0d0d;
    background: ${(props) => props.theme.colors.primary};
    border-radius: 1.3px;
    border: 0.2px solid #010101;
  }

  &:focus {
    outline: none;
  }

  &::-ms-track {
    width: 100%;
    cursor: pointer;

    background: transparent;
    border-color: transparent;
    color: transparent;
  }
`

const ScaleValue = styled.div`
  margin: 0 auto;
  margin-top: 2rem;
  font-size: 16pt;
  font-weight: 500;
  flex-shrink: 1;
  text-align: center;
  color: ${(props) => props.theme.colors.primary};
`

interface ScaleProps {
  isDisabled?: boolean
  isReadOnly?: boolean
  isValid?: boolean
  variant?: TextFieldVariant
  id: string
  name: string
  value?: string
  onChange?: (e: React.FormEvent<HTMLInputElement>) => void
}

export const Scale: React.FunctionComponent<ScaleProps> = (props) => {
  const sliderValue = props.value || '50'
  const displayValue = props.value || ''

  return (
    <React.Fragment>
      <ScaleInput
        disabled={props.isDisabled}
        type={`range`}
        min={0}
        max={100}
        id={props.id}
        name={props.name}
        value={sliderValue}
        onChange={props.onChange}
      />
      <ScaleValue>{displayValue}</ScaleValue>
    </React.Fragment>
  )
}
